import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import devolucionVentaService, { DevolucionVentaItem } from '../../services/devolucionVentaService';
import PDFViewer from '../shared/PDFViewer';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { formatComprobanteTesoreria, formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import FiltroSucursalVentaSelect from '../shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

const ESTADO_DEVOLUCION_OPTIONS = ['activos', 'anulados', 'todos'] as const;
type FiltroEstadoDevolucion = (typeof ESTADO_DEVOLUCION_OPTIONS)[number];

interface DevolucionesFiltrosStored {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroEstado?: FiltroEstadoDevolucion;
  filtroNroSuc?: number | null;
  rowsPerPage?: number;
}

const formatComprobante = (row: DevolucionVentaItem): string =>
  formatComprobanteTesoreria(row.letra || 'X', row.nroSuc, row.nroComprobante);

const Devoluciones: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  const filtrosGuardados = () => {
    const parsed = loadTesoreriaFiltros<DevolucionesFiltrosStored>(TESORERIA_FILTROS_KEYS.devoluciones);
    if (parsed.filtroEstado && !ESTADO_DEVOLUCION_OPTIONS.includes(parsed.filtroEstado)) {
      parsed.filtroEstado = 'activos';
    }
    return parsed;
  };

  const [items, setItems] = useState<DevolucionVentaItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(filtrosGuardados(), 'rowsPerPage', 10));
  const [searchTerm, setSearchTerm] = useState(() => storedString(filtrosGuardados(), 'searchTerm', ''));
  const [fechaDesde, setFechaDesde] = useState(() =>
    storedString(filtrosGuardados(), 'fechaDesde', getFirstDayOfMonth())
  );
  const [fechaHasta, setFechaHasta] = useState(() =>
    storedString(filtrosGuardados(), 'fechaHasta', getLastDayOfMonth())
  );
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstadoDevolucion>(
    () => filtrosGuardados().filtroEstado ?? 'activos'
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedDevolucion, setSelectedDevolucion] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro({
    resetPreferidaOnNavigation: true,
  });

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const response = await api.get(`/api/DevolucionesVenta/${nroFormulario}/pdf`, { responseType: 'blob' });
      const cd = (response.headers as Record<string, string | undefined>)?.['content-disposition'];
      setPdfFileName(getFileNameFromContentDisposition(cd));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedDevolucion(nroFormulario);
    } catch {
      enqueueSnackbar('Error al generar el PDF de la devolución', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedDevolucion(null);
    setPdfFileName(null);
  };

  const fetchDevoluciones = async () => {
    try {
      const params: Record<string, string | number> = {
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      };
      if (searchTerm.trim()) params.searchTerm = searchTerm.trim();
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;
      if (filtroEstado === 'anulados') params.asentado = 'A';
      else if (filtroEstado === 'activos') params.asentado = 'activos';
      const nroSucValue = resolveNroSucFiltro(filtroNroSuc);
      if (nroSucValue != null) params.nroSuc = nroSucValue;

      const response = await devolucionVentaService.list(params);
      setItems(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      const msg = error?.response?.data?.mensaje || error?.response?.data?.message || 'Error al cargar devoluciones';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.devoluciones,
    { searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage },
    [searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage]
  );

  useEffect(() => {
    if (!filtroListo) return;
    fetchDevoluciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc, filtroListo]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tesoreria/recibos')} size="small" sx={{ textTransform: 'none' }}>
                  Recibos
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Devoluciones
                </Typography>
              </Stack>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/tesoreria/devoluciones/nuevo')}>
                Nueva devolución
              </Button>
            </Stack>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por cliente o número..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select value={filtroEstado} label="Estado" onChange={(e) => { setFiltroEstado(e.target.value as typeof filtroEstado); setPage(0); }}>
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="anulados">Anulados</MenuItem>
                    <MenuItem value="todos">Todos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No hay devoluciones para los filtros seleccionados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const anulado = (row.asentado ?? '').trim().toUpperCase() === 'A' || (row.nombre ?? '').trim().toUpperCase() === 'ANULADO';
                      return (
                        <TableRow key={row.nroFormulario} hover>
                          <TableCell>{formatComprobante(row)}</TableCell>
                          <TableCell>{row.fechaEmision ? new Date(row.fechaEmision).toLocaleDateString('es-AR') : '-'}</TableCell>
                          <TableCell>{row.nroCliente} - {row.nombre}</TableCell>
                          <TableCell align="right">{formatCurrency(row.importeTotal ?? 0)}</TableCell>
                          <TableCell>{row.usuario || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={anulado ? 'Anulado' : 'Activo'} color={anulado ? 'default' : 'success'} variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Imprimir PDF">
                              <IconButton
                                onClick={() => handleImprimirPDF(row.nroFormulario)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                  transition: 'all 0.2s',
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Filas por página"
            />
          </Stack>
        </CardContent>
      </Card>

      {pdfUrl && selectedDevolucion && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Devolución ${selectedDevolucion}`}
          documentId={`${selectedDevolucion}`}
          documentType="devolucion"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}
    </Box>
  );
};

export default Devoluciones;
